/* oxlint-disable no-console -- IndexedDB/SQLite persistence errors; module has no TelemetryPort */
import { AppConfig } from '@core/domain/AppConfig.ts';
import initSqlJs, { type Database, type SqlJsConfig } from 'sql.js';

/**
 * Session PRAGMA: FKs enabled, safer delete (fewer PII remnants when wiping rows), cache tuning.
 * No WAL journal: pairing sql.js persistence with IndexedDB avoids auxiliary WAL quirks.
 */
function configureSqliteClientSession(db: Database): boolean {
  try {
    db.run('PRAGMA foreign_keys = ON');
    db.run('PRAGMA secure_delete = ON');
    db.run('PRAGMA cache_size = -8000');
    db.run('PRAGMA temp_store = MEMORY');
    db.run('PRAGMA synchronous = NORMAL');
    return migrateLocalSqliteSchema(db);
  } catch (e) {
    console.error('[local-sqlite] session PRAGMA', e);
    return false;
  }
}
import { getSqlJsWasmBinary } from 'sqljs-wasm-browser-binary';
import { getEmbeddedLocalDbSnapshot } from 'embedded-local-db-snapshot';
import { withRetry, withTimeout } from '@core/domain/utils/asyncResilience.ts';
// Browser: DDL+seeds ship in `local.db`; bump LOCAL_SQLITE_SNAPSHOT_REVISION on schema change (see README.md#docker).
import {
  didSnapshotRevisionChange,
  noteSnapshotRevisionUpgradeIfNeeded,
  readStoredSnapshotRevision,
  writeStoredSnapshotRevision,
} from '@src/modules/platform/local-sqlite/infrastructure/snapshotRevisionGuard.ts';
import { migrateLocalSqliteSchema } from '@src/modules/platform/local-sqlite/infrastructure/localSqliteSchemaMigration.ts';
import { decodeSqliteIdbBytes } from '@src/modules/platform/local-sqlite/infrastructure/sqliteIdbCodec.ts';
import {
  bindLocalSqlitePersistState,
  isLocalSqliteDirty,
  type LocalSqlitePersistState,
} from '@src/modules/platform/local-sqlite/infrastructure/localSqlitePersistDirty.ts';
import { persistSqliteExportToIdb } from '@src/modules/platform/local-sqlite/infrastructure/sqliteIdbPersistClient.ts';
import { notifyLocalSqlitePeerTabs } from '@src/modules/platform/local-sqlite/infrastructure/localSqlitePeerSync.ts';
import {
  deleteLocalSqliteIndexedDb,
  getLocalSqliteIdbBytes,
  getLocalSqliteIdbBytesAtKey,
  putLocalSqliteIdbBytesAtKey,
} from '@src/modules/platform/local-sqlite/infrastructure/sqliteIdbStorage.ts';
import { notifyLocalSqlitePersistError } from '@src/modules/platform/local-sqlite/infrastructure/localSqlitePersistError.ts';
import { mergePendingWorkFromBackupDb } from '@src/modules/platform/local-sqlite/infrastructure/recoveryMergePendingWork.ts';
import {
  clearRecoveryBackupPresent,
  clearRecoveryMergeFailed,
  markRecoveryBackupPresent,
  markRecoveryMergeFailed,
} from '@src/modules/platform/local-sqlite/domain/recoveryMergeFlags.ts';

export { deleteLocalSqliteIndexedDb };

/** Treat as recoverable snapshot HTTP failures (network, 5xx, 429); do not retry definitive 403/404. */
class SnapshotHttpTransientError extends Error {
  /** Constructs this instance. */

  constructor(readonly status: number) {
    super(`Snapshot HTTP ${status}`);
    this.name = 'SnapshotHttpTransientError';
  }
}

const IDB_RECOVERY_BACKUP_KEY_SUFFIX = '.recovery-backup';

/**
 * Preserves unreadable SQLite bytes under a backup key so unsynced local rows are not destroyed
 * by the snapshot reseed; deletes the IndexedDB database only if the backup write itself fails
 * (otherwise the later reseed put could fail against a broken store).
 */
function recoveryBackupKey(): string {
  return AppConfig.getLocalSqliteIdbSqliteKey() + IDB_RECOVERY_BACKUP_KEY_SUFFIX;
}

async function backupUnreadableIdbBytes(bytes: Uint8Array): Promise<void> {
  const backupKey = recoveryBackupKey();
  try {
    await putLocalSqliteIdbBytesAtKey(backupKey, bytes);
    markRecoveryBackupPresent();
  } catch (backupErr) {
    console.error('[local-sqlite] failed to back up unreadable bytes; deleting IndexedDB', backupErr);
    try {
      await deleteLocalSqliteIndexedDb();
    } catch (delErr) {
      console.error('[local-sqlite] failed to delete IndexedDB after open failure', delErr);
    }
  }
}

/**
 * After snapshot reseed, restore pending entregas/reposiciones from `*.recovery-backup` when present.
 */
async function tryMergePendingFromRecoveryBackup(
  SQL: Awaited<ReturnType<typeof initSqlJs>>,
  target: Database,
): Promise<void> {
  const backupKey = recoveryBackupKey();
  let backupBytes: Uint8Array | undefined;
  try {
    backupBytes = await getLocalSqliteIdbBytesAtKey(backupKey);
  } catch (e) {
    console.warn('[local-sqlite] could not read recovery backup', e);
    markRecoveryMergeFailed(e instanceof Error ? e.message : String(e));
    return;
  }
  if (backupBytes == null || backupBytes.byteLength === 0) {
    clearRecoveryBackupPresent();
    clearRecoveryMergeFailed();
    return;
  }
  markRecoveryBackupPresent();
  let backupDb: Database | undefined;
  try {
    backupDb = new SQL.Database(backupBytes);
    backupDb.run('SELECT 1');
    const result = mergePendingWorkFromBackupDb(target, backupDb);
    if (!result.ok) {
      markRecoveryMergeFailed(result.error);
      return;
    }
    clearRecoveryMergeFailed();
    if (result.rowsCopied > 0) {
      console.info(
        `[local-sqlite] recovery merge restored ${result.rowsCopied} rows across ${result.tablesCopied} tables`,
      );
    }
    // Keep backup bytes until export / successful sync path clears the flag explicitly.
  } catch (e) {
    console.error('[local-sqlite] recovery backup open/merge failed', e);
    markRecoveryMergeFailed(e instanceof Error ? e.message : String(e));
  } finally {
    try {
      backupDb?.close();
    } catch {
      /* ignore */
    }
  }
}

export const SQLJS_WASM_NOT_EMBEDDED_MSG =
  'Este build no incluye WASM de SQLite embebido (caché antigua). Recompile, borre datos del sitio y recargue con red.';

const SQLJS_WASM_HTTP_PREFIX = '/sqljs/';

/** Production/PWA: WASM inlined at build. Dev preview: fetch from `/sqljs/*` copied into dist. */
function sqlJsInitOptions(): SqlJsConfig {
  const wasmBinary = getSqlJsWasmBinary();
  if (wasmBinary != null && wasmBinary.byteLength > 0) {
    return { wasmBinary };
  }
  if (import.meta.env.SQLJS_WASM_INLINE === '1') {
    throw new Error(SQLJS_WASM_NOT_EMBEDDED_MSG);
  }
  return { locateFile: (file) => `${SQLJS_WASM_HTTP_PREFIX}${file}` };
}

/**
 * Asserts that the database is readable by executing a simple query.
 */
function assertDatabaseReadable(db: Database): void {
  try {
    db.run('SELECT 1');
  } catch (e) {
    try {
      db.close();
    } catch {
      /* ignore persistence failures writing revision */
    }
    throw new Error(
      `Local SQLite database is not readable after open: ${e instanceof Error ? e.message : String(e)}`,
    );
  }
}

/**
 * Initializes the sql.js engine.
 */
async function initSqlEngine(): Promise<Awaited<ReturnType<typeof initSqlJs>>> {
  return withRetry(
    (attempt) =>
      withTimeout(
        initSqlJs(sqlJsInitOptions()),
        attempt === 0 ? 35_000 : 45_000,
        'sql.js (WASM) load timed out',
      ),
    {
      maxAttempts: 6,
      baseDelayMs: 300,
      maxDelayMs: 6000,
      jitterRatio: 0.25,
      isRetryable: (err) => {
        const m = err instanceof Error ? err.message : String(err);
        if (/404/.test(m) && /\.wasm|sql-wasm|sql\.js/i.test(m)) return false;
        return true;
      },
    },
  );
}

/**
 * Retrieves bytes from IndexedDB with retry logic.
 */
async function idbGetBytesResilient(): Promise<Uint8Array | undefined> {
  return withRetry(() => getLocalSqliteIdbBytes(), {
    maxAttempts: 5,
    baseDelayMs: 80,
    maxDelayMs: 2500,
    jitterRatio: 0.2,
  });
}

/**
 * Checks if the HTTP status indicates a missing snapshot.
 */
function isSnapshotMissingMessage(status: number): boolean {
  return status === 404 || status === 403;
}

/**
 * Fetches the snapshot buffer from the given URL.
 */
async function fetchSnapshotBuffer(dbUrl: string, attemptIndex: number): Promise<ArrayBuffer> {
  const sep = dbUrl.includes('?') ? '&' : '?';
  const url =
    attemptIndex === 0 ? dbUrl : `${dbUrl}${sep}_r=${attemptIndex}&_t=${Date.now()}&cache=bypass`;
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) {
    if (isSnapshotMissingMessage(res.status)) {
      throw new Error(
        `Failed to load ${dbUrl} (${res.status}). Run db:init-local (see deploy docs), then rebuild or start dev so database/local.db is copied to dist.`,
      );
    }
    if (res.status >= 500 || res.status === 429 || res.status === 408) {
      throw new SnapshotHttpTransientError(res.status);
    }
    throw new Error(`Failed to load ${dbUrl} (${res.status}).`);
  }
  return res.arrayBuffer();
}
/**
 * Opens sql.js DB from an in-memory snapshot (embedded bundle bytes or fetched file).
 */
function openDatabaseFromSnapshotBuffer(SQL: Awaited<ReturnType<typeof initSqlJs>>, buf: Uint8Array): Database {
  const db = new SQL.Database(buf);
  configureSqliteClientSession(db);
  assertDatabaseReadable(db);
  return db;
}

/**
 * Opens a database from the build-inlined snapshot (offline-first) or fetched `dbUrl` (dev / remote API mode).
 */
async function openDatabaseFromSnapshot(SQL: Awaited<ReturnType<typeof initSqlJs>>, dbUrl: string): Promise<Database> {
  if (AppConfig.useLocalSqliteClient()) {
    const embedded = getEmbeddedLocalDbSnapshot();
    if (embedded != null && embedded.byteLength > 0) {
      return openDatabaseFromSnapshotBuffer(SQL, embedded);
    }
  }
  return withRetry(
    async (attemptIndex) => {
      const buf = new Uint8Array(await fetchSnapshotBuffer(dbUrl, attemptIndex));
      return openDatabaseFromSnapshotBuffer(SQL, buf);
    },
    {
      maxAttempts: 6,
      baseDelayMs: 400,
      maxDelayMs: 8000,
      jitterRatio: 0.25,
      isRetryable: (err) => {
        if (err instanceof SnapshotHttpTransientError) return true;
        if (err instanceof TypeError) return true;
        return false;
      },
    },
  );
}

/**
 * Opens a database instance from IndexedDB bytes.
 */
async function openDatabaseFromIdb(
  SQL: Awaited<ReturnType<typeof initSqlJs>>,
  bytes: Uint8Array,
  persistState: LocalSqlitePersistState,
): Promise<Database> {
  persistState.idbCache.bytes = bytes;
  persistState.dirty = false;
  const sqliteBytes = await decodeSqliteIdbBytes(bytes);
  const db = new SQL.Database(sqliteBytes);
  const migrated = configureSqliteClientSession(db);
  assertDatabaseReadable(db);
  if (migrated) {
    try {
      persistState.dirty = true;
      await persistSqliteExportToIdb(db.export(), persistState.idbCache, persistState);
    } catch (e) {
      console.error('[local-sqlite] persist after schema migration', e);
    }
  }
  return db;
}

/**
 * Loads sql.js-backed SQLite for the PE module: prefers IndexedDB (local mutations); otherwise
 * seeds from the build-inlined snapshot when present, else fetches `LOCAL_SQLITE_DB_URL`.
 * Schema changes: bump {@link LOCAL_SQLITE_SNAPSHOT_REVISION} and regenerate `local.db`. Reseed from
 * snapshot when IDB is empty or open fails; unreadable bytes are copied to a `*.recovery-backup` key first.
 */
export async function loadLocalSqlDatabase(dbUrl: string, persistState: LocalSqlitePersistState = { idbCache: {}, dirty: false }): Promise<Database> {
  const SQL = await initSqlEngine();
  const previousRevision = readStoredSnapshotRevision();
  const skipIdbForRevisionUpgrade = didSnapshotRevisionChange(previousRevision);
  let fromIdb: Uint8Array | undefined;
  if (skipIdbForRevisionUpgrade) {
    const stale = await idbGetBytesResilient();
    if (stale != null && stale.byteLength > 0) {
      console.warn(
        '[local-sqlite] snapshot revision changed; backing up IndexedDB bytes and reseeding from embedded snapshot',
      );
      await backupUnreadableIdbBytes(stale);
    }
  } else {
    fromIdb = await idbGetBytesResilient();
  }

  if (fromIdb != null && fromIdb.byteLength > 0) {
    try {
      const db = await openDatabaseFromIdb(SQL, fromIdb, persistState);
      writeStoredSnapshotRevision();
      noteSnapshotRevisionUpgradeIfNeeded(previousRevision);
      return db;
    } catch (e) {
      console.error(
        '[local-sqlite] IDB open failed; backing up local bytes and reseeding from snapshot',
        e,
      );
      await backupUnreadableIdbBytes(fromIdb);
    }
  }

  const db = await openDatabaseFromSnapshot(SQL, dbUrl);
  writeStoredSnapshotRevision();
  noteSnapshotRevisionUpgradeIfNeeded(previousRevision);
  await tryMergePendingFromRecoveryBackup(SQL, db);
  try {
    persistState.dirty = true;
    await persistSqliteExportToIdb(db.export(), persistState.idbCache, persistState);
  } catch (e) {
    console.error('[local-sqlite] initial persist after snapshot', e);
    notifyLocalSqlitePersistError(e);
  }
  return db;
}

type SqlPersistHandle = {
  schedule: () => void;
  flush: () => Promise<void>;
};

/** Coalesce rapid SQLite writes before full db.export() → IndexedDB. */
export const LOCAL_SQLITE_PERSIST_DEBOUNCE_MS = 600;

function createDebouncedSqlPersist(
  db: Database,
  persistState: LocalSqlitePersistState,
  onPersisted?: () => void,
  debounceMs = LOCAL_SQLITE_PERSIST_DEBOUNCE_MS,
): SqlPersistHandle {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> | null = null;

  const write = (): Promise<void> => {
    if (inFlight != null) return inFlight;
    if (!persistState.dirty) return Promise.resolve();
    inFlight = (async () => {
      try {
        await persistSqliteExportToIdb(db.export(), persistState.idbCache, persistState);
        onPersisted?.();
      } catch (e) {
        console.error('[local-sqlite] persist', e);
        notifyLocalSqlitePersistError(e);
        throw e;
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  };

  const schedule = (): void => {
    if (timer != null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      // write() already calls notifyLocalSqlitePersistError before rethrow
      void write().catch(() => undefined);
    }, debounceMs);
  };

  const flush = async (): Promise<void> => {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
    await write();
  };

  return { schedule, flush };
}

export type LocalSqliteSession = {
  db: Database;
  persistState: LocalSqlitePersistState;
  persist: () => void;
  flushPersist: () => Promise<void>;
};

function onLocalSqlitePersistedToIdb(): void {
  notifyLocalSqlitePeerTabs();
}

export async function openLocalSqliteSession(snapshotUrl: string): Promise<LocalSqliteSession> {
  const persistState: LocalSqlitePersistState = { idbCache: {}, dirty: false };
  bindLocalSqlitePersistState(persistState);
  const db = await loadLocalSqlDatabase(snapshotUrl, persistState);
  const handle = createDebouncedSqlPersist(db, persistState, onLocalSqlitePersistedToIdb);
  return { db, persistState, persist: handle.schedule, flushPersist: handle.flush };
}

export async function reloadLocalSqliteSessionFromIdb(session: LocalSqliteSession): Promise<LocalSqliteSession> {
  if (isLocalSqliteDirty()) return session;
  try {
    session.db.close();
  } catch {
    /* ignore close errors */
  }
  const bytes = await idbGetBytesResilient();
  if (bytes == null || bytes.byteLength === 0) return session;
  const SQL = await initSqlEngine();
  const db = await openDatabaseFromIdb(SQL, bytes, session.persistState);
  const handle = createDebouncedSqlPersist(db, session.persistState, onLocalSqlitePersistedToIdb);
  return { db, persistState: session.persistState, persist: handle.schedule, flushPersist: handle.flush };
}

export type LocalSqlConnection = {
  db: Database;
  persist: () => void;
  flushPersist: () => Promise<void>;
};

/** Opens sql.js with IndexedDB persistence from the snapshot URL (debounced `persist` callback). */
export async function openLocalSqlConnectionFromSnapshot(
  snapshotUrl: string,
): Promise<LocalSqlConnection> {
  const session = await openLocalSqliteSession(snapshotUrl);
  return { db: session.db, persist: session.persist, flushPersist: session.flushPersist };
}
